/**
 * measurements.json: the flat row schema and the parse dispatch of PredictionObject.Json.cs /
 * ParseJSON. Positions are converted to the scene frame (mm, (x, y, z) -> (x, z, y)).
 */
import * as THREE from 'three';
import { principalDiameters } from '@/corrections/geometry';
import { fileToScene } from '@/theater/frame';

export interface MeasurementRow {
  Category: string;
  Structure: string;
  Name: string;
  Index: number | null;
  Pair: string;
  World_X_mm: number | null;
  World_Y_mm: number | null;
  World_Z_mm: number | null;
  Height_to_VBR_mm: number | null;
  Commissural_Height_mm: number | null;
  VBR_Height_mm: number | null;
  P1_Index: number | null;
  P2_Index: number | null;
  P1_X_mm: number | null;
  P1_Y_mm: number | null;
  P1_Z_mm: number | null;
  P2_X_mm: number | null;
  P2_Y_mm: number | null;
  P2_Z_mm: number | null;
  Value: number | string | null;
  Units: string;
  Source: string;
  Notes: string;
  Frame_Index?: number | null;
  Configuration?: string | null;
}

export interface PointEntry {
  structure: string;
  name: string;
  position: THREE.Vector3; // scene mm
  heightToVbr: number | null;
  commissuralHeight: number | null;
  frame: number;
  units: string;
}

export interface PlaneEntry {
  structure: string;
  center: THREE.Vector3 | null;
  normal: THREE.Vector3 | null; // unit, scene frame
  frame: number;
}

export interface DiameterEntry {
  structure: string;
  name: string;
  value: number | null;
  p1: THREE.Vector3 | null;
  p2: THREE.Vector3 | null;
  units: string;
}

export interface MetricEntry {
  category: 'Metric' | 'Distance';
  structure: string;
  name: string;
  pair: string;
  value: number | string | null;
  units: string;
  notes: string;
  source: string;
  p1: THREE.Vector3 | null;
  p2: THREE.Vector3 | null;
  heightToVbr: number | null;
}

export interface Prediction {
  rows: MeasurementRow[];
  primaryFrame: number;
  planes: Map<string, PlaneEntry>; // VBR, STJ, SOV
  points: PointEntry[];
  rings: Map<string, THREE.Vector3[]>; // STRUCTURE (upper) -> loop in file order
  curves: Map<string, THREE.Vector3[]>; // structure -> ordered by Index
  diameters: Map<string, DiameterEntry>; // "STRUCT|name"
  metrics: MetricEntry[];
  angles: { R: number | null; L: number | null; N: number | null };
  fusedMargins: Map<string, string>; // free-margin structure -> dominant pair from Notes
  valveConfiguration: { detected: string | null; available: string[] };
}

const CURVE_SKIP = ['FreeMargin_Cusps', 'CoaptationDeficiencyPlane', 'CoaptationLength', 'RootRotationInteratrial', 'RootRotationRlCommissure', 'AorticValveOpening'];

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function vec(x: unknown, y: unknown, z: unknown): THREE.Vector3 | null {
  const a = num(x);
  const b = num(y);
  const c = num(z);
  if (a === null || b === null || c === null) return null;
  return fileToScene(a, b, c);
}

export function findPoint(p: Prediction, structure: string, name: string, frame?: number): PointEntry | undefined {
  const f = frame ?? p.primaryFrame;
  return (
    p.points.find((x) => x.structure === structure && x.name === name && x.frame === f) ??
    p.points.find((x) => x.structure === structure && x.name === name)
  );
}

export function findMetric(p: Prediction, structure: string, name: string): MetricEntry | undefined {
  return p.metrics.find((m) => m.structure === structure && m.name === name);
}

/** ValveConfigurationState.SelectEntries: keep untagged rows (the detected reading); when the
 * user picks the other reading and it is available, swap the free-margin rows. */
export function selectConfiguration(rows: MeasurementRow[], active: string | null): { rows: MeasurementRow[]; detected: string | null; available: string[] } {
  let detected: string | null = null;
  const available: string[] = [];
  for (const r of rows) {
    if (r.Category === 'Metric' && r.Structure === 'ValveConfiguration') {
      if (r.Notes && !detected) detected = r.Notes;
      if ((r.Name === 'bicuspid' || r.Name === 'trileaflet') && typeof r.Value === 'number' && r.Value >= 0.5) available.push(r.Name);
    }
  }
  const useOther = active && active !== detected && available.includes(active);
  const isFreeMargin = (s: string) => /_FreeMargin/.test(s);
  const out = rows.filter((r) => {
    if (r.Category === 'Metric' && r.Structure === 'ValveConfiguration') return false;
    const tag = r.Configuration || '';
    if (!tag) return !(useOther && isFreeMargin(r.Structure));
    return useOther && tag === active && isFreeMargin(r.Structure);
  });
  return { rows: out, detected, available };
}

export function parseMeasurements(raw: MeasurementRow[], activeConfiguration: string | null = null): Prediction {
  const sel = selectConfiguration(raw, activeConfiguration);
  const rows = sel.rows;
  const planes = new Map<string, PlaneEntry>();
  const points: PointEntry[] = [];
  const rings = new Map<string, THREE.Vector3[]>();
  const ringFrames = new Map<string, number>();
  const curves = new Map<string, { index: number; p: THREE.Vector3 }[]>();
  const diameters = new Map<string, DiameterEntry>();
  const metrics: MetricEntry[] = [];
  const angles = { R: null as number | null, L: null as number | null, N: null as number | null };
  const fusedMargins = new Map<string, string>();

  let primaryFrame = 0;
  for (const r of rows) {
    if (r.Category === 'Plane' && r.Structure === 'VBR' && r.Name === 'normal_unit') {
      primaryFrame = r.Frame_Index ?? 0;
      break;
    }
  }

  const frameOf = (r: MeasurementRow) => r.Frame_Index ?? 0;

  for (const r of rows) {
    if (!r.Category || !r.Structure) continue;
    const frame = frameOf(r);
    switch (r.Category) {
      case 'Plane': {
        const key = r.Structure;
        const entry = planes.get(key) ?? { structure: key, center: null, normal: null, frame };
        if (r.Name === 'center') entry.center = vec(r.World_X_mm, r.World_Y_mm, r.World_Z_mm);
        else if (r.Name === 'normal_unit') {
          const n = vec(r.World_X_mm, r.World_Y_mm, r.World_Z_mm);
          entry.normal = n ? n.normalize() : null;
        }
        if (frame === primaryFrame || !planes.has(key)) planes.set(key, entry);
        break;
      }
      case 'Point': {
        const pos = vec(r.World_X_mm, r.World_Y_mm, r.World_Z_mm);
        if (!pos) break;
        points.push({
          structure: r.Structure,
          name: r.Name,
          position: pos,
          heightToVbr: num(r.Height_to_VBR_mm),
          commissuralHeight: num(r.Commissural_Height_mm),
          frame,
          units: r.Units,
        });
        break;
      }
      case 'RingPoint': {
        if (r.Structure === 'PulmonaryAnnulus') break;
        const key = r.Structure.toUpperCase();
        const pos = vec(r.World_X_mm, r.World_Y_mm, r.World_Z_mm);
        if (!pos) break;
        // One frame seeds the display: the first seen, except VBR follows the primary frame.
        const seeded = ringFrames.get(key);
        if (seeded === undefined) ringFrames.set(key, frame);
        else if (seeded !== frame) {
          if (key === 'VBR' && frame === primaryFrame) {
            ringFrames.set(key, frame);
            rings.set(key, []);
          } else break;
        }
        const list = rings.get(key) ?? [];
        list.push(pos);
        rings.set(key, list);
        break;
      }
      case 'CurvePoint': {
        if (CURVE_SKIP.some((s) => r.Structure.includes(s))) break;
        const pos = vec(r.World_X_mm, r.World_Y_mm, r.World_Z_mm);
        if (!pos) break;
        const list = curves.get(r.Structure) ?? [];
        const idx = r.Index !== null && r.Index > 0 ? r.Index : list.length + 1;
        list.push({ index: idx, p: pos });
        curves.set(r.Structure, list);
        break;
      }
      case 'Diameter': {
        if (r.Structure === 'PulmonaryAnnulus') break;
        diameters.set(`${r.Structure.toUpperCase()}|${r.Name}`, {
          structure: r.Structure.toUpperCase(),
          name: r.Name,
          value: num(r.Value),
          p1: vec(r.P1_X_mm, r.P1_Y_mm, r.P1_Z_mm),
          p2: vec(r.P2_X_mm, r.P2_Y_mm, r.P2_Z_mm),
          units: r.Units,
        });
        break;
      }
      case 'Metric':
      case 'Distance': {
        metrics.push({
          category: r.Category,
          structure: r.Structure,
          name: r.Name,
          pair: r.Pair,
          value: typeof r.Value === 'number' || typeof r.Value === 'string' ? r.Value : null,
          units: r.Units,
          notes: r.Notes,
          source: r.Source,
          p1: vec(r.P1_X_mm, r.P1_Y_mm, r.P1_Z_mm),
          p2: vec(r.P2_X_mm, r.P2_Y_mm, r.P2_Z_mm),
          heightToVbr: num(r.Height_to_VBR_mm),
        });
        if (r.Name === 'fused' && typeof r.Value === 'number' && r.Value >= 0.5) {
          const m = /dominant\s+(\w+)/.exec(r.Notes ?? '');
          fusedMargins.set(r.Structure, m ? m[1] : '');
        }
        break;
      }
      case 'Angle': {
        if (r.Structure !== 'Commissural') break;
        const key = r.Pair || r.Name;
        const v = num(r.Value);
        if (v === null) break;
        if (key === 'RN-RL' || key === 'RL-RN') angles.R = Math.abs(v);
        else if (key === 'RL-LN' || key === 'LN-RL') angles.L = Math.abs(v);
        else if (key === 'LN-RN' || key === 'RN-LN') angles.N = Math.abs(v);
        break;
      }
    }
  }

  const sortedCurves = new Map<string, THREE.Vector3[]>();
  for (const [k, list] of curves) {
    list.sort((a, b) => a.index - b.index);
    sortedCurves.set(k, list.map((x) => x.p));
  }

  return {
    rows,
    primaryFrame,
    planes,
    points,
    rings,
    curves: sortedCurves,
    diameters,
    metrics,
    angles,
    fusedMargins,
    valveConfiguration: { detected: sel.detected, available: sel.available },
  };
}

// ------------------------------------------------------------------ ring maths (MeasureRing)

export interface RingMetrics {
  centroid: THREE.Vector3;
  normal: THREE.Vector3;
  perimeterMm: number;
  areaMm2: number;
  maxChord: { a: number; b: number; length: number };
  minChord: { a: number; b: number; length: number };
}

/** Newell's method (ComputeRingNormal). */
export function ringNormal(pts: THREE.Vector3[]): THREE.Vector3 {
  if (pts.length < 3) return new THREE.Vector3(0, 1, 0);
  const n = new THREE.Vector3();
  for (let i = 0; i < pts.length; i++) {
    const c = pts[i];
    const nx = pts[(i + 1) % pts.length];
    n.x += (c.y - nx.y) * (c.z + nx.z);
    n.y += (c.z - nx.z) * (c.x + nx.x);
    n.z += (c.x - nx.x) * (c.y + nx.y);
  }
  return n.lengthSq() < 1e-6 ? new THREE.Vector3(0, 1, 0) : n.normalize();
}

export function ringCentroid(pts: THREE.Vector3[]): THREE.Vector3 {
  const c = new THREE.Vector3();
  for (const p of pts) c.add(p);
  return c.multiplyScalar(1 / Math.max(1, pts.length));
}

/** MeasureRing: centroid, Newell normal, principal diameters, perimeter, projected area. */
export function measureRing(pts: THREE.Vector3[]): RingMetrics | null {
  if (pts.length < 3) return null;
  const centroid = ringCentroid(pts);
  const normal = ringNormal(pts);
  let u = new THREE.Vector3().crossVectors(normal, new THREE.Vector3(0, 1, 0)).normalize();
  if (u.lengthSq() < 1e-6) u = new THREE.Vector3().crossVectors(normal, new THREE.Vector3(1, 0, 0)).normalize();
  const v = new THREE.Vector3().crossVectors(normal, u).normalize();
  const pts2 = pts.map((p) => {
    const rel = p.clone().sub(centroid);
    return [rel.dot(u), rel.dot(v)] as [number, number];
  });
  // ComputePrincipalDiametersIndices: antiparallel pairs about the centroid, 3D chord lengths
  const principal = principalDiameters(pts2);
  const max = { a: principal.max.a, b: principal.max.b, length: pts[principal.max.a].distanceTo(pts[principal.max.b]) };
  const min = { a: principal.min.a, b: principal.min.b, length: pts[principal.min.a].distanceTo(pts[principal.min.b]) };
  let perimeter = 0;
  for (let i = 0; i < pts.length; i++) perimeter += pts[i].distanceTo(pts[(i + 1) % pts.length]);
  let area2 = 0;
  for (let i = 0; i < pts2.length; i++) {
    const [x1, y1] = pts2[i];
    const [x2, y2] = pts2[(i + 1) % pts2.length];
    area2 += x1 * y2 - x2 * y1;
  }
  return { centroid, normal, perimeterMm: perimeter, areaMm2: Math.abs(area2) * 0.5, maxChord: max, minChord: min };
}

/** Closed centripetal Catmull-Rom (GenerateClosedCentripetalCatmullRom). */
export function closedCatmullRom(control: THREE.Vector3[], samplesPerSegment = 20, alpha = 0.5): THREE.Vector3[] {
  if (control.length < 3) return control.slice();
  const n = control.length;
  const safeSamples = Math.max(4, samplesPerSegment);
  const a = Math.min(1, Math.max(0, alpha));
  const getT = (t: number, p0: THREE.Vector3, p1: THREE.Vector3) => t + Math.pow(Math.max(1e-4, p0.distanceTo(p1)), a);
  const lerpSafe = (p: THREE.Vector3, q: THREE.Vector3, t0: number, t1: number, t: number) => {
    const denom = t1 - t0;
    if (Math.abs(denom) < 1e-5) return p.clone();
    return p.clone().lerp(q, (t - t0) / denom);
  };
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = control[(i - 1 + n) % n];
    const p1 = control[i];
    const p2 = control[(i + 1) % n];
    const p3 = control[(i + 2) % n];
    const t0 = 0;
    const t1 = getT(t0, p0, p1);
    const t2 = getT(t1, p1, p2);
    const t3 = getT(t2, p2, p3);
    for (let j = 0; j < safeSamples; j++) {
      const t = t1 + (t2 - t1) * (j / safeSamples);
      const a1 = lerpSafe(p0, p1, t0, t1, t);
      const a2 = lerpSafe(p1, p2, t1, t2, t);
      const a3 = lerpSafe(p2, p3, t2, t3, t);
      const b1 = lerpSafe(a1, a2, t0, t2, t);
      const b2 = lerpSafe(a2, a3, t1, t3, t);
      out.push(lerpSafe(b1, b2, t1, t2, t));
    }
  }
  return out;
}

export function polylineLength(pts: THREE.Vector3[], closed = false): number {
  let l = 0;
  for (let i = 0; i + 1 < pts.length; i++) l += pts[i].distanceTo(pts[i + 1]);
  if (closed && pts.length > 1) l += pts[pts.length - 1].distanceTo(pts[0]);
  return l;
}

// ------------------------------------------------------------------ formatting (MeasurementDisplay)

export function formatLengthMm(mm: number): string {
  return `${(mm / 10).toFixed(1)} cm`;
}
export function formatAreaMm2(mm2: number): string {
  return `${(mm2 / 100).toFixed(1)} cm²`;
}
export function formatAngle(deg: number): string {
  return `${deg.toFixed(1)}°`;
}
export function formatMeasurement(value: number, units: string): string {
  const u = (units ?? '').trim().toLowerCase();
  if (!u) return value.toFixed(2);
  if (['mm', 'millimeter', 'millimeters', 'millimetre', 'millimetres'].includes(u)) return formatLengthMm(value);
  if (['mm²', 'mm^2', 'mm2'].includes(u)) return formatAreaMm2(value);
  if (u === 'cm') return `${value.toFixed(1)} cm`;
  if (['cm²', 'cm^2', 'cm2'].includes(u)) return `${value.toFixed(1)} cm²`;
  if (['deg', 'degree', 'degrees', '°'].includes(u)) return formatAngle(value);
  return `${value.toFixed(2)} ${units}`;
}
